import type { ReactNode } from 'react'

/** The record-form controls' look, shared by the operator and admin New forms. */
export const FORM_INPUT_CLASS = 'w-full border border-marine/25 bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:border-marine disabled:bg-canvas disabled:text-ink-mid'

interface FieldProps {
  label: string
  htmlFor: string
  /** Shown after the label; every field says which it is, since the forms show required and optional fields alike. */
  required?: boolean
  hint?: string
  /** Spans both columns of a two-column grid. */
  span?: boolean
  children: ReactNode
}

/** A labelled control with its hint, marked Required or Optional so the whole record is visible at a glance. */
export function Field({ label, htmlFor, required = false, hint, span = false, children }: FieldProps) {
  return (
    <div className={span ? 'sm:col-span-2' : undefined}>
      <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor={htmlFor}>
        {label} <span className={`font-normal ${required ? 'text-coral' : 'text-ink-mid/70'}`}>{required ? 'Required' : 'Optional'}</span>
      </label>
      {children}
      {hint && <p className="text-[11px] text-ink-mid mt-1">{hint}</p>}
    </div>
  )
}

/** The failure of a create call, in the API's words where it has any. */
export function FormError({ error, fallback }: { error: unknown; fallback: string }) {
  if (!error) {
    return null
  }
  const message = error instanceof Error && error.message !== '' ? error.message : fallback
  return (
    <p role="alert" className="text-xs font-bold text-coral">
      {message}
    </p>
  )
}

/** A record id from a display name, in the form the API accepts: lower-case letters, digits and hyphens. */
export function slugId(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** The API's own rule for a facilityId or workerId. */
export const RECORD_ID_PATTERN = '[A-Za-z0-9-]+'
export const RECORD_ID_HINT = 'Letters, digits and hyphens only. Cannot be changed later.'
